// services/newsService.ts
import http from '@/utils/http' // Assuming you have a configured http client
import { CommentResponse, NewsResponse, PostResponse } from '@/models/news.model'

/**
 * Fetch news articles with pagination and optional menuSlug filter.
 */
export const fetchNews = async (
  page: number = 1,
  size: number = 10,
  menuSlug?: string
): Promise<NewsResponse> => {
  const response = await http.get<NewsResponse>('news', {
    params: { page, size, menuSlug },
  })
  return response
}

export const searchNews = async ({
  page = 1,
  size = 10,
  menuSlug = '',
  search = '',
}: {
  page?: number
  size?: number
  menuSlug?: string
  search?: string
}): Promise<NewsResponse> => {
  const response = await http.get<NewsResponse>('/news', {
    params: { page, size, menuSlug, search },
  })
  return response
}

export const fetchPost = async (slug: string = ''): Promise<PostResponse> => {
  const response = await http.get<PostResponse>(`/admin-console/news/${slug}`)
  return response
}

// Comment API
export const fetchComments = async (
  newsId: string = '',
  raoVatId: string = ''
): Promise<CommentResponse> => {
  const response = await http.get<CommentResponse>('/comments', {
    params: { newsId, raoVatId },
  })
  return response // Ensure this matches the CommentResponse type
}

export const addComment = async ({
  newsId,
  raoVatId,
  content,
  anonymousEmail,
  anonymousName,
}: {
  newsId?: string
  raoVatId?: string
  content: string
  anonymousEmail?: string
  anonymousName?: string
}): Promise<void> => {
  await http.post('/comments', {
    newsId,
    raoVatId,
    content,
    anonymousEmail,
    anonymousName,
  })
}
export const fetchArticle = async (slug: string): Promise<PostResponse | null> => {
  if (!slug) return null
  // bỏ qua các đuôi file tĩnh (sw.js, robots.txt, sitemap.xml, …)
  if (/\.[a-z0-9]+$/i.test(slug)) return null

  const url = `${(process.env.NEXT_PUBLIC_BACKEND_URL || '').replace(/\/$/, '')}/api/admin-console/news/${encodeURIComponent(slug)}`
  try {
    const res = await fetch(url, {
      next: { revalidate: 60 },
      headers: { Accept: 'application/json' },
    })

    // Backend mới có thể trả 400 khi không có bài → coi như null
    if (!res.ok) {
      const text = await res.text()
      let body
      try {
        body = JSON.parse(text)
      } catch {}
      const notFound =
        res.status === 404 || (res.status === 400 && /not found/i.test(body?.message || text))
      if (notFound) return null
      throw new Error(`HTTP ${res.status} ${res.statusText}${text ? ` – ${text}` : ''}`)
    }

    return await res.json()
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    console.error('[fetchArticle]', { slug, url, message, cause: err })
    // QUAN TRỌNG: đừng gộp chuỗi như cũ
    throw new Error(message)
  }
}

export const fetchNewsList = async (
  page: number = 1,
  size: number = 10,
  menuSlug: string = ''
): Promise<NewsResponse | null> => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_BACKEND_URL}/api/news?page=${page}&size=${size}&menuSlug=${menuSlug}`,
      {
        next: { revalidate: 60 }, // Cache trong 60 giây (Next.js 14)
      }
    )

    if (!response.ok) throw new Error('Failed to fetch post')

    return response.json()
  } catch (error) {
    throw Error('Error fetching post:' + error)
  }
}
